// Libellés traduisibles. 12 langues cibles du dossier : FR et EN sont relus ; les autres sont des traductions
// complètes signalées « à relire » dans les paramètres (aucune promesse de traduction native sans relecture).
// FR et EN sont inclus dans le paquet principal ; les autres langues sont chargées à la demande.
import { fr } from './fr';
import { en } from './en';
import { setCatalog, type CatalogDict } from './catalog';

export const LANGS = ['fr', 'en', 'es', 'pt-BR', 'it', 'de', 'ru', 'zh-Hans', 'ja', 'ko', 'ar', 'hi'] as const;
export type Lang = (typeof LANGS)[number];

/** Nom de chaque langue dans sa propre langue (sélecteur). */
export const LANG_NAMES: Record<Lang, string> = {
  fr: 'Français',
  en: 'English',
  es: 'Español',
  'pt-BR': 'Português (Brasil)',
  it: 'Italiano',
  de: 'Deutsch',
  ru: 'Русский',
  'zh-Hans': '简体中文',
  ja: '日本語',
  ko: '한국어',
  ar: 'العربية',
  hi: 'हिन्दी',
};

/** Langues relues ; les autres affichent la mention « traduction à relire ». */
export const REVIEWED_LANGS: readonly Lang[] = ['fr', 'en'];
const RTL_LANGS: ReadonlySet<Lang> = new Set(['ar']);

export type Key = keyof typeof fr;
/** Formes plurielles (règles Intl.PluralRules de la langue) ; `other` est obligatoire. */
export interface Plural {
  zero?: string;
  one?: string;
  two?: string;
  few?: string;
  many?: string;
  other: string;
}
export type Dict = Record<Key, string | Plural>;

interface Pack {
  ui: Dict;
  catalog: CatalogDict | null;
}

const PACKS: Partial<Record<Lang, Pack>> = { fr: { ui: fr, catalog: null } };
/** Langues disponibles : une langue n'apparaît que lorsque sa traduction est complète. */
const LOADERS: Partial<Record<Lang, () => Promise<Pack>>> = {
  fr: async () => ({ ui: fr, catalog: null }),
  en: async () => ({ ui: en, catalog: (await import('./catalog-en')).catalog }),
  es: async () => ({ ui: (await import('./es')).ui, catalog: (await import('./catalog-es')).catalog }),
  'pt-BR': async () => ({ ui: (await import('./pt-BR')).ui, catalog: (await import('./catalog-pt-BR')).catalog }),
  it: async () => ({ ui: (await import('./it')).ui, catalog: (await import('./catalog-it')).catalog }),
  de: async () => ({ ui: (await import('./de')).ui, catalog: (await import('./catalog-de')).catalog }),
  ru: async () => ({ ui: (await import('./ru')).ui, catalog: (await import('./catalog-ru')).catalog }),
  'zh-Hans': async () => ({ ui: (await import('./zh-Hans')).ui, catalog: (await import('./catalog-zh-Hans')).catalog }),
  ja: async () => ({ ui: (await import('./ja')).ui, catalog: (await import('./catalog-ja')).catalog }),
  ko: async () => ({ ui: (await import('./ko')).ui, catalog: (await import('./catalog-ko')).catalog }),
  ar: async () => ({ ui: (await import('./ar')).ui, catalog: (await import('./catalog-ar')).catalog }),
  hi: async () => ({ ui: (await import('./hi')).ui, catalog: (await import('./catalog-hi')).catalog }),
};
export const AVAILABLE_LANGS: readonly Lang[] = LANGS.filter((l) => l in LOADERS);

let current: Lang = 'fr';
const listeners = new Set<() => void>();

export function isLang(v: unknown): v is Lang {
  return typeof v === 'string' && (LANGS as readonly string[]).includes(v);
}

/** Langue du navigateur parmi les 12, sinon anglais. */
export function detectLang(): Lang {
  const prefs = typeof navigator !== 'undefined' ? (navigator.languages?.length ? navigator.languages : [navigator.language]) : ['fr'];
  for (const raw of prefs) {
    const n = raw.toLowerCase();
    const base = n.split('-')[0];
    const hit = AVAILABLE_LANGS.find((l) => l === base || (base === 'pt' && l === 'pt-BR') || (base === 'zh' && l === 'zh-Hans'));
    if (hit) return hit;
  }
  return 'en';
}

/** Change de langue (chargement à la demande) ; en cas d'échec de chargement, reste sur la langue actuelle. */
export async function setLang(l: Lang): Promise<void> {
  const load = LOADERS[l];
  if (!load) return;
  if (!PACKS[l]) {
    try {
      PACKS[l] = await load();
    } catch {
      return;
    }
  }
  current = l;
  setCatalog(PACKS[l]!.catalog);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = l;
    document.documentElement.dir = RTL_LANGS.has(l) ? 'rtl' : 'ltr';
  }
  for (const fn of listeners) fn();
}

export function getLang(): Lang {
  return current;
}

export function onLangChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Code de langue pour Intl (nombres, dates, pluriels). */
export function locale(): string {
  return current === 'fr' ? 'fr-CH' : current;
}

export function t(key: Key, params?: Record<string, string | number>): string {
  let v: string | Plural = PACKS[current]?.ui[key] ?? PACKS.en?.ui[key] ?? fr[key] ?? key;
  if (typeof v !== 'string') {
    const n = typeof params?.n === 'number' ? params.n : 0;
    const form = new Intl.PluralRules(locale()).select(n) as keyof Plural;
    v = v[form] ?? v.other;
  }
  let s = v;
  if (params) for (const [k, val] of Object.entries(params)) s = s.replaceAll(`{${k}}`, formatParam(val));
  return s;
}

function formatParam(v: string | number): string {
  return typeof v === 'number' ? new Intl.NumberFormat(locale()).format(v) : v;
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat(locale()).format(Math.floor(n));
}

/** Date ou heure dans le format local de la langue choisie (les limites restent stockées en UTC). */
export function formatDate(ts: number, kind: 'date' | 'time' | 'datetime' = 'date'): string {
  const d = new Date(ts);
  if (kind === 'time') return d.toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit' });
  if (kind === 'datetime') return d.toLocaleString(locale(), { dateStyle: 'medium', timeStyle: 'short' });
  return d.toLocaleDateString(locale(), { dateStyle: 'medium' });
}

export function formatMoney(cents: number, currency = 'CHF'): string {
  return new Intl.NumberFormat(locale(), { style: 'currency', currency }).format(cents / 100);
}
